import classnames from "classnames";
import { MutableRefObject, ReactNode } from "react";
import { Button, Col, Dropdown, Row } from "react-bootstrap";
import CustomLinkButton from "../../components/Button/CustomLinkButton.tsx";
import ButtonWithSimpleModal from "../Button/ButtonWithSimpleModal.tsx";
import ConfirmButtonWithModal from "../Button/ConfirmButtonWithModal.tsx";
import DeleteButton from "../Button/DeleteButton.tsx";
import { IconClose, IconDelete, IconEdit, IconSee } from "../Icon/Icon.tsx";
import DeleteModal from "../Modal/DeleteModal.tsx";
import EditModal from "../Modal/EditModal.tsx";
import useModal from "../Modal/ModalUtils.tsx";
import TooltipCustom from "../Tooltip/Tooltip.tsx";

const TableActionColumn = ({
  row,
  disabled = false,
  textDisable,
  textEnable,
  classEnable = "primary",
  deleteModal = null,
  editModal = null,
  confirmModal = null,
  simpleModal = null,
  isPost = true,
  reload,
  icon,
  pathname,
  hide = () => false,
  onClick,
  isLink = true,
  state,
  search,
}: TableActionButtonType) => {
  return (
    !hide ||
    (!hide(row.original) && (
      <Col xs={1}>
        <>
          {editModal != null ? (
            <>
              <EditModal
                closeModal={editModal.close}
                header={editModal.header}
                ref={editModal.ref}
                visible={editModal.visible}
                query={editModal.path}
                prepareVariables={editModal.prepareVariable}
                value={editModal.value}
                onSubmit={() =>
                  reload ? reload() : window.location.reload(false)
                }
              >
                {editModal.content}
              </EditModal>

              <TooltipCustom
                tooltipText={disabled ? textDisable : textEnable}
                tooltipId={row.value}
              >
                <Button
                  variant="link"
                  className={classnames(
                    "p-0 m-0",
                    disabled
                      ? "text-decoration-none text-muted"
                      : "text-decoration-none text-" + classEnable,
                  )}
                  disabled={disabled}
                  onClick={editModal?.show}
                >
                  {icon}
                </Button>
              </TooltipCustom>
            </>
          ) : deleteModal != null ? (
            <>
              <TooltipCustom
                tooltipText={disabled ? textDisable : textEnable}
                tooltipId={row.value}
              >
                <DeleteButton
                  className={classnames(
                    "p-0 m-0",
                    disabled ? "text-muted" : "text-" + classEnable,
                  )}
                  disabled={disabled}
                  onClick={deleteModal?.show}
                />
              </TooltipCustom>
              {!disabled && (
                <DeleteModal
                  visible={deleteModal.visible}
                  closeModal={deleteModal.close}
                  query={pathname}
                  ref={deleteModal.ref}
                  onDelete={() =>
                    reload ? reload() : window.location.reload(false)
                  }
                  header={deleteModal.header}
                  content={deleteModal.content}
                />
              )}
            </>
          ) : confirmModal != null ? (
            <ConfirmButtonWithModal
              path={pathname}
              icon={icon}
              disabled={disabled}
              classEnable={classEnable}
              textDisable={textDisable}
              textEnable={textEnable}
              tooltipId={row.value}
              isPost={isPost}
              header={confirmModal.header}
              content={confirmModal.content}
            />
          ) : simpleModal != null ? (
            <ButtonWithSimpleModal
              icon={icon}
              disabled={disabled}
              classEnable={classEnable}
              textDisable={textDisable}
              textEnable={textEnable}
              tooltipId={row.value}
              header={simpleModal.header}
              content={simpleModal.content}
            />
          ) : isLink ? (
            <TooltipCustom
              tooltipText={disabled ? textDisable : textEnable}
              tooltipId={row.value}
            >
              <Col>
                <CustomLinkButton
                  search={search}
                  variant={"link"}
                  className={classnames(
                    "p-0 m-0",
                    "text-decoration-none",
                    disabled ? "text-muted" : "text-" + classEnable,
                  )}
                  disabled={disabled}
                  pathname={pathname}
                  onClick={onClick}
                  state={state}
                >
                  {icon}
                </CustomLinkButton>
              </Col>
            </TooltipCustom>
          ) : (
            <TooltipCustom
              tooltipText={disabled ? textDisable : textEnable}
              tooltipId={row.value}
            >
              <Button
                variant={"link"}
                className={classnames(
                  "p-0 m-0",
                  "text-decoration-none",
                  disabled ? "text-muted" : "text-" + classEnable,
                )}
                disabled={disabled}
                href={pathname}
                onClick={onClick}
              >
                {icon}
              </Button>
            </TooltipCustom>
          )}
        </>
      </Col>
    ))
  );
};

type TableActionButtonType = {
  row?: any;
  disable?: (row?: any) => boolean;
  disabled?: boolean;
  textDisable?: string;
  textDisableFunction?: (row?: any) => string;
  conditionnalTextDisable?: (row?: any) => string;
  textEnable?: string;
  icon?: ReactNode;
  classEnable?: "primary" | "danger" | "warning" | "info" | "success";
  reload?: () => void;
  query?;
  confirmModal?: SimpleModalType | null;
  deleteModal?: object | null;
  editModal?: EditModalType | null;
  simpleModal?: SimpleModalType | null;
  state?: any | undefined;
  search?: (param: any) => string | null;
  hide?: (param: any) => boolean;
  onClick?: (param?: any, row?: any) => any;
  isPost?: boolean;
  pathname?: string | ((row: object) => string);
  isLink?: boolean;
  header?: (row: any) => string | null;
  content?: (row: any) => ReactNode | null;
};

type ModaleType = {
  visible?: boolean;
  show?: (value?: any) => void;
  close?: () => void;
  ref?: MutableRefObject<HTMLDialogElement | null>;
  header?: string | ReactNode | ((row: any) => string | ReactNode);
  content?: ReactNode | ((row: any) => ReactNode);
};

type SimpleModalType = {
  header: string | ((row: any) => string);
  content: ReactNode | ((id: string, row?: any) => ReactNode);
};

type ResolvedSimpleModalType = {
  header: string;
  content: ReactNode;
};

type EditModalType = ModaleType & {
  header?: (row: any) => string;
  content?: (id: string) => ReactNode;
  path: (row: any) => string;
  prepareVariable: (row: any) => any;
  value?: any;
};

export const ActionButton = ({
  buttons,
  row,
}: {
  buttons: Array<ButtonType>;
  row: any;
}) => {
  return (
    <Row className={"d-flex flex-row"}>
      {buttons.map((_button, key) => {
        switch (_button.type) {
          case TYPE_BUTTON.DELETE:
            return (
              <DeleteButtonPrivate key={key} _button={_button} row={row} />
            );
          case TYPE_BUTTON.CONFIRM:
            return (
              <ConfirmButtonPrivate key={key} _button={_button} row={row} />
            );
          case TYPE_BUTTON.SIMPLE_MODAL:
            return (
              <SimpleModalButtonPrivate key={key} _button={_button} row={row} />
            );
          case TYPE_BUTTON.EDIT_MODAL:
            return (
              <EditModalButtonPrivate key={key} _button={_button} row={row} />
            );
          case TYPE_BUTTON.DROPDOWN:
            return (
              <DropdownButtonPrivate key={key} _button={_button} row={row} />
            );
          case TYPE_BUTTON.UPDATE:
            return (
              <TableActionColumn
                key={key}
                row={row}
                textEnable={_button.textEnable ?? "Modifier"}
                icon={<IconEdit />}
                classEnable={"info"}
                textDisable={_button.textDisable}
                disabled={_button.disable ? _button.disable(row) : false}
                pathname={_button.route?.(row.value)}
                onClick={() => _button.onClick?.(row.value, row.original)}
                state={_button.state}
                isLink={true}
              />
            );
          case TYPE_BUTTON.SEE:
            return (
              <TableActionColumn
                key={key}
                row={row}
                textEnable={_button.textEnable ?? "Voir plus"}
                icon={_button.icon ?? <IconSee />}
                pathname={_button.route?.(row.value)}
              />
            );
          case TYPE_BUTTON.LINK:
            return (
              <TableActionColumn
                key={key}
                row={row}
                textEnable={_button.textEnable}
                icon={_button.icon}
                pathname={_button.route?.(row.value)}
                onClick={() => _button.onClick?.(row.value, row.original)}
                hide={_button.hide}
                disabled={_button.disable ? _button.disable(row) : false}
                classEnable={_button.classEnable}
                confirmModal={_button.confirmModal}
                reload={_button.reload}
                textDisable={_button.textDisable}
                search={_button.search?.(row)}
                state={_button.state?.(row)}
              />
            );
          case TYPE_BUTTON.BUTTON:
            return (
              <TableActionColumn
                key={key}
                row={row}
                textEnable={_button.textEnable}
                icon={_button.icon}
                pathname={_button.route?.(row.value)}
                onClick={() => _button.onClick?.(row.value, row.original)}
                hide={_button.hide}
                disabled={_button.disable ? _button.disable(row) : false}
                classEnable={_button.classEnable}
                confirmModal={_button.confirmModal}
                reload={_button.reload}
                textDisable={_button.textDisable}
                isLink={false}
              />
            );
        }
      })}
    </Row>
  );
};

export type ButtonType = TableActionButtonType & {
  route?: (param: any) => string;
  type: TYPE_BUTTON;
  children?: Array<ButtonType>;
};

export enum TYPE_BUTTON {
  DELETE,
  CONFIRM,
  SIMPLE_MODAL,
  UPDATE,
  SEE,
  LINK,
  BUTTON,
  EDIT_MODAL,
  DROPDOWN,
}

type DeleteButtonType = { row: any; _button: ButtonType };

const EditModalButtonPrivate = ({ row, _button }: DeleteButtonType) => {
  const { visible, show, close, ref } = useModal();
  const editModal: EditModalType = {
    close: close,
    ref: ref,
    show: show,
    visible: visible,
    content: _button.editModal?.content(row.value),
    header: _button.editModal?.header(row),
    path: _button.editModal.path(row),
    prepareVariable: _button.editModal.prepareVariable,
    value: _button.editModal.value,
  };
  return (
    <TableActionColumn
      row={row}
      disabled={_button.disable ? _button.disable(row) : false}
      editModal={editModal}
      textDisable={_button.textDisable}
      textEnable={_button.textEnable ?? "Editer"}
      icon={_button.icon ?? <IconClose />}
    />
  );
};

const DeleteButtonPrivate = ({ row, _button }: DeleteButtonType) => {
  const { visible, show, close, ref } = useModal();
  const deleteModal: ModaleType = {
    close: close,
    ref: ref,
    show: show,
    visible: visible,
    header: _button.header ? _button.header(row) : undefined,
    content: _button.content ? _button.content(row) : undefined,
  };
  let computedPathname = "";
  if (typeof _button.pathname === "function") {
    computedPathname = _button.pathname(row);
  } else if (typeof _button.pathname === "string") {
    computedPathname = `${_button.pathname}${row.value ?? ""}`;
  }
  return (
    <TableActionColumn
      row={row}
      disabled={_button.disable ? _button.disable(row) : false}
      textDisable={_button.textDisable ?? _button.textDisableFunction?.(row)}
      textEnable={_button.textEnable ?? "Supprimer"}
      classEnable={"danger"}
      deleteModal={deleteModal}
      icon={<IconDelete />}
      pathname={computedPathname}
    />
  );
};

type ConfirmButtonType = { row: any; _button: ButtonType };
const ConfirmButtonPrivate = ({ row, _button }: ConfirmButtonType) => {
  const confirmModal: SimpleModalType = {
    header:
      typeof _button.confirmModal?.header === "function"
        ? _button.confirmModal.header(row)
        : (_button.confirmModal?.header ?? "Confirmer ?"),
    content:
      typeof _button.confirmModal?.content === "function"
        ? _button.confirmModal.content(row.value, row)
        : (_button.confirmModal?.content ?? "Confirmer ?"),
  };
  return (
    <TableActionColumn
      isPost={_button.isPost}
      row={row}
      disabled={_button.disable ? _button.disable(row) : false}
      textDisable={
        _button.textDisable ?? _button.conditionnalTextDisable?.(row)
      }
      hide={_button.hide}
      textEnable={_button.textEnable}
      classEnable={_button.classEnable}
      confirmModal={confirmModal}
      icon={_button.icon ?? <IconClose />}
      pathname={`${_button.pathname}${row.value}`}
    />
  );
};

type SimpleModalButtonType = {
  row: any;
  _button: ButtonType;
};
const SimpleModalButtonPrivate = ({ row, _button }: SimpleModalButtonType) => {
  const contentValue =
    typeof _button.simpleModal?.content === "function"
      ? _button.simpleModal.content(row.value, row)
      : _button.simpleModal?.content;

  const headerValue =
    typeof _button.simpleModal?.header === "function"
      ? _button.simpleModal.header(row)
      : _button.simpleModal?.header;

  const simpleModal: ResolvedSimpleModalType = {
    content: contentValue,
    header: headerValue || "",
  };
  return (
    <TableActionColumn
      row={row}
      simpleModal={simpleModal}
      textEnable={_button.textEnable ?? "Voir plus"}
      icon={_button.icon ?? <IconClose />}
      disabled={_button.disable ? _button.disable(row) : false}
      textDisable={_button.textDisable}
    />
  );
};

type DropdownButtonType = { row: any; _button: ButtonType };
const DropdownButtonPrivate = ({ row, _button }: DropdownButtonType) => {
  const isHidden = _button.hide ? _button.hide(row) : false;
  if (isHidden) {
    return null;
  }

  const items = (_button.children ?? []).filter((child) =>
    child.hide ? !child.hide(row) : true,
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Col xs={1}>
      <Dropdown align="end">
        <TooltipCustom
          tooltipText={_button.textEnable}
          tooltipId={`dropdown-${row.value}`}
        >
          <Dropdown.Toggle
            variant="link"
            className={classnames(
              "p-0 m-0",
              "text-decoration-none",
              _button.classEnable
                ? `text-${_button.classEnable}`
                : "text-primary",
            )}
          >
            {_button.icon ?? <IconSee />}
          </Dropdown.Toggle>
        </TooltipCustom>
        <Dropdown.Menu>
          {items.map((child, idx) => {
            const disabled = child.disable ? child.disable(row) : false;
            const key = `${row.value}-child-${idx}`;
            const label = child.textEnable ?? "Action";
            const icon = child.icon;

            if (child.type === TYPE_BUTTON.LINK) {
              const pathname = child.route?.(row.value);
              return (
                <Dropdown.Item
                  as="span"
                  key={key}
                  className={classnames(disabled && "disabled")}
                >
                  <CustomLinkButton
                    variant="link"
                    className={classnames(
                      "p-0 m-0",
                      disabled ? "text-muted" : "",
                    )}
                    disabled={disabled}
                    pathname={pathname ?? "#"}
                    onClick={() => child.onClick?.(row.value, row.original)}
                    state={child.state}
                    search={child.search?.(row) ?? undefined}
                  >
                    {icon}&nbsp;{label}
                  </CustomLinkButton>
                </Dropdown.Item>
              );
            }

            return (
              <Dropdown.Item
                key={key}
                as="button"
                className={classnames(
                  "d-flex align-items-center",
                  disabled && "disabled",
                )}
                disabled={disabled}
                onClick={() => child.onClick?.(row.value, row.original)}
              >
                {icon}
                <span className="ms-2">{label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </Col>
  );
};
